import "./morph-strip.css";

import { isCompactViewport } from "./responsive.ts";
import { GLYPHS, type GlyphFn } from "./selector.ts";
import {
  MORPH_MAX,
  MORPH_MIN,
  MORPH_NAMES,
  formatMorph,
  pathMorphedWave,
  sharedPhase,
} from "./morph-shared.ts";

// MorphStrip — a single continuous control for the four-archetype morph.
// Layout (top to bottom):
//   [ recessed preview well, animated phase, ice-blue stroke ]
//   [ ∿     /\     ⊓⊓     /| ]            <- four glyph anchors
//   [ ─────────●─────────────────── ]      <- recessed track + puck
//   [ readout · label ]
//
// The puck position is the morph value. Click any anchor to animate the
// puck to that integer position. Anchors closest to the puck brighten with
// a proximity glow so the active region is obvious without snapping the
// continuous value.

const ANCHOR_GLYPHS: GlyphFn[] = [
  GLYPHS.sine,
  GLYPHS.triangle,
  GLYPHS.square,
  GLYPHS.saw,
];

const CELL_SIZE_DEFAULT = 36;
const CELL_SIZE_COMPACT = 26;

/** Proximity over which an anchor's glow falls from 1 → 0 in morph units. */
const PROXIMITY_HALF = 0.5;

/** Time constant for the snap-to-anchor animation when an anchor is clicked. */
const SNAP_TAU_MS = 90;

export interface MorphStripOptions {
  container: HTMLElement;
  /** Preview well width in CSS px. Defaults to 320 (240 in compact mode).
   *  In `fluid` mode this is treated as the *maximum* width. */
  width?: number;
  /** Preview well height in CSS px. Defaults to 64 (44 in compact mode). */
  previewHeight?: number;
  /** Initial morph value in [0, 3]. Defaults to 0 (sine). */
  value?: number;
  /** Persistent label below the strip. */
  label?: string;
  /** Compact mode for in-module placement (e.g. inside an oscillator
   *  cell). Reduces preview height, anchor-cell size, and default width
   *  to keep the strip from dominating the module's vertical space. */
  compact?: boolean;
  /** When true, the strip's outer width becomes `min(width, 100%)` and a
   *  ResizeObserver keeps the preview canvas re-drawn whenever the actual
   *  rendered width changes — for mobile / responsive layouts. */
  fluid?: boolean;
  onChange?: (morph: number) => void;
}

export class MorphStrip {
  private root: HTMLDivElement;
  private preview: HTMLDivElement;
  private previewCanvas: HTMLCanvasElement;
  private previewCtx: CanvasRenderingContext2D;
  private trackRegion: HTMLDivElement;
  private anchorRow: HTMLDivElement;
  private anchorCanvases: HTMLCanvasElement[] = [];
  private track: HTMLDivElement;
  private fill: HTMLDivElement;
  private puck: HTMLDivElement;
  private readout: HTMLOutputElement;

  private width: number;
  private maxWidth: number;
  private previewHeight: number;
  private cellSize: number;
  private fluid: boolean;
  private morph: number;
  private snapTarget: number | null = null;
  private onChange: ((m: number) => void) | undefined;
  private resizeObserver: ResizeObserver | null = null;

  private dragging = false;
  private dragRect: DOMRect | null = null;
  private rafHandle = 0;
  private lastTime = 0;

  constructor(opts: MorphStripOptions) {
    // Auto-enable compact mode on phone-class viewports so a desktop call
    // site that didn't set `compact` still gets a touch-friendly footprint.
    const compact = opts.compact ?? isCompactViewport();
    this.cellSize = compact ? CELL_SIZE_COMPACT : CELL_SIZE_DEFAULT;
    this.fluid = opts.fluid ?? false;
    this.maxWidth = opts.width ?? (compact ? 240 : 320);
    this.width = this.maxWidth;
    this.previewHeight = opts.previewHeight ?? (compact ? 44 : 64);
    this.morph = clamp(opts.value ?? 0, MORPH_MIN, MORPH_MAX);
    if (opts.onChange) this.onChange = opts.onChange;

    this.root = document.createElement("div");
    this.root.className = "morph-strip";
    if (compact) this.root.classList.add("is-compact");
    if (this.fluid) {
      this.root.classList.add("is-fluid");
      // Fluid sizing: shrink to container, capped at the desktop width
      // (= preview width plus the outer padding allowance).
      const outerCap = this.maxWidth + (compact ? 20 : 32);
      this.root.style.width = "100%";
      this.root.style.maxWidth = `${outerCap}px`;
    } else {
      // Compact mode tightens the outer padding too, so the .morph-strip
      // wrapper grows by less than the default `width + 32`.
      this.root.style.width = `${this.width + (compact ? 20 : 32)}px`;
    }

    // Preview well. Width is `100%` of the strip wrapper in fluid mode so
    // it tracks the responsive container; otherwise pinned to the
    // construction-time pixel width.
    this.preview = document.createElement("div");
    this.preview.className = "morph-strip-preview";
    this.preview.style.width = this.fluid ? "100%" : `${this.width}px`;
    this.preview.style.height = `${this.previewHeight}px`;
    this.previewCanvas = document.createElement("canvas");
    this.previewCanvas.style.width = this.fluid ? "100%" : `${this.width}px`;
    this.previewCanvas.style.height = `${this.previewHeight}px`;
    const pctx = this.previewCanvas.getContext("2d");
    if (!pctx) throw new Error("morph-strip: 2d context unavailable");
    this.previewCtx = pctx;
    this.preview.append(this.previewCanvas);

    // Track region (anchor row + track).
    this.trackRegion = document.createElement("div");
    this.trackRegion.className = "morph-strip-track-region";
    this.trackRegion.style.width = this.fluid ? "100%" : `${this.width}px`;

    this.anchorRow = document.createElement("div");
    this.anchorRow.className = "morph-strip-anchors";

    for (let i = 0; i < ANCHOR_GLYPHS.length; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "morph-strip-anchor";
      btn.setAttribute("aria-label", MORPH_NAMES[i]!);
      btn.style.left = `${this.anchorLeftPercent(i)}%`;
      const canvas = document.createElement("canvas");
      canvas.width = this.cellSize;
      canvas.height = this.cellSize;
      canvas.style.width = `${this.cellSize}px`;
      canvas.style.height = `${this.cellSize}px`;
      btn.append(canvas);
      btn.addEventListener("click", () => this.beginSnapTo(i));
      this.anchorCanvases.push(canvas);
      this.anchorRow.append(btn);
    }

    this.track = document.createElement("div");
    this.track.className = "morph-strip-track";
    this.track.tabIndex = 0;
    this.track.setAttribute("role", "slider");
    this.track.setAttribute("aria-valuemin", String(MORPH_MIN));
    this.track.setAttribute("aria-valuemax", String(MORPH_MAX));
    this.track.setAttribute("aria-label", "waveform morph");

    this.fill = document.createElement("div");
    this.fill.className = "morph-strip-fill";

    this.puck = document.createElement("div");
    this.puck.className = "morph-strip-puck";

    this.track.append(this.fill, this.puck);

    this.trackRegion.append(this.anchorRow, this.track);

    this.readout = document.createElement("output");
    this.readout.className = "morph-strip-readout";
    this.readout.textContent = formatMorph(this.morph);

    this.root.append(this.preview, this.trackRegion, this.readout);

    if (opts.label) {
      const lbl = document.createElement("span");
      lbl.className = "label morph-strip-label";
      lbl.textContent = opts.label;
      this.root.append(lbl);
    }

    opts.container.append(this.root);

    this.attachInteraction();
    this.layout();
    this.scheduleAnimation();

    // Fluid mode: observe the preview well and update `this.width` plus
    // the cached track rect so anchor positioning, pointer drag math,
    // and the preview canvas all stay aligned with the responsive
    // layout. The DPR-scaled canvas inside `drawPreview()` already
    // reads `this.width` per call, so updating that field is enough.
    if (this.fluid) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const e of entries) {
          const w = e.contentRect.width;
          if (w > 0 && Math.abs(w - this.width) > 0.5) {
            this.width = w;
            this.dragRect = null; // force recompute on next pointer event
          }
        }
      });
      this.resizeObserver.observe(this.preview);
    }
  }

  getValue(): number {
    return this.morph;
  }

  setValue(v: number, animate = false): void {
    const next = clamp(v, MORPH_MIN, MORPH_MAX);
    if (animate) {
      this.snapTarget = next;
      this.scheduleAnimation();
      return;
    }
    if (next === this.morph) return;
    this.morph = next;
    this.snapTarget = null;
    this.layout();
    this.onChange?.(next);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.root.remove();
  }

  // ----- interaction -----

  private attachInteraction(): void {
    this.track.addEventListener("pointerdown", (ev) => {
      ev.preventDefault();
      this.track.setPointerCapture(ev.pointerId);
      this.dragging = true;
      this.snapTarget = null;
      this.dragRect = this.track.getBoundingClientRect();
      this.applyFromPointer(ev);
    });
    this.track.addEventListener("pointermove", (ev) => {
      if (!this.dragging) return;
      this.applyFromPointer(ev);
    });
    const endDrag = (ev: PointerEvent): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.releasePointerCapture(ev.pointerId);
    };
    this.track.addEventListener("pointerup", endDrag);
    this.track.addEventListener("pointercancel", endDrag);

    this.track.addEventListener("keydown", (ev) => {
      const step = ev.shiftKey ? 0.02 : 0.1;
      if (ev.key === "ArrowRight" || ev.key === "ArrowUp") {
        ev.preventDefault();
        this.setValue(this.morph + step);
      } else if (ev.key === "ArrowLeft" || ev.key === "ArrowDown") {
        ev.preventDefault();
        this.setValue(this.morph - step);
      } else if (ev.key === "Home") {
        ev.preventDefault();
        this.setValue(MORPH_MIN);
      } else if (ev.key === "End") {
        ev.preventDefault();
        this.setValue(MORPH_MAX);
      }
    });
  }

  private applyFromPointer(ev: PointerEvent): void {
    const rect = this.dragRect ?? this.track.getBoundingClientRect();
    const t = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
    this.setValue(MORPH_MIN + t * (MORPH_MAX - MORPH_MIN));
  }

  private beginSnapTo(idx: number): void {
    this.snapTarget = idx;
    this.scheduleAnimation();
  }

  // ----- layout + draw -----

  private anchorLeftPercent(idx: number): number {
    return (idx / (ANCHOR_GLYPHS.length - 1)) * 100;
  }

  private layout(): void {
    const t = (this.morph - MORPH_MIN) / (MORPH_MAX - MORPH_MIN);
    const pct = t * 100;
    this.puck.style.left = `${pct}%`;
    this.fill.style.width = `calc(${pct}% - 2px)`;
    this.readout.textContent = formatMorph(this.morph);
    this.track.setAttribute("aria-valuenow", this.morph.toFixed(2));
    this.drawAnchors();
  }

  private drawAnchors(): void {
    for (let i = 0; i < ANCHOR_GLYPHS.length; i++) {
      const canvas = this.anchorCanvases[i]!;
      const proximity = clamp(
        1 - Math.abs(this.morph - i) / PROXIMITY_HALF,
        0,
        1,
      );
      drawAnchorGlyph(canvas, ANCHOR_GLYPHS[i]!, proximity, this.cellSize);
    }
  }

  private drawPreview(phase: number): void {
    const dpr = window.devicePixelRatio || 1;
    const px = this.width * dpr;
    const py = this.previewHeight * dpr;
    if (this.previewCanvas.width !== px || this.previewCanvas.height !== py) {
      this.previewCanvas.width = px;
      this.previewCanvas.height = py;
    }
    const ctx = this.previewCtx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.width, this.previewHeight);

    // Faint horizontal centerline catches the eye like an oscilloscope.
    ctx.save();
    ctx.strokeStyle = "rgba(56, 189, 248, 0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, this.previewHeight / 2);
    ctx.lineTo(this.width, this.previewHeight / 2);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.shadowColor = "rgba(56, 189, 248, 0.7)";
    ctx.shadowBlur = 10;
    ctx.strokeStyle = "#7dd3fc";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    pathMorphedWave(ctx, this.morph, {
      width: this.width,
      height: this.previewHeight,
      amplitudeFrac: 0.7,
      cycles: 2,
      phaseOffset: phase,
    });
    ctx.stroke();
    ctx.restore();
  }

  private scheduleAnimation(): void {
    if (this.rafHandle) return;
    this.lastTime = performance.now();
    const tick = (now: number): void => {
      const dt = now - this.lastTime;
      this.lastTime = now;

      // Resolve snap-to-anchor animation if one is in flight.
      if (this.snapTarget !== null) {
        const coef = SNAP_TAU_MS > 0 ? 1 - Math.exp(-dt / SNAP_TAU_MS) : 1;
        const target = this.snapTarget;
        const next = this.morph + (target - this.morph) * coef;
        if (Math.abs(next - target) < 1e-3) {
          this.morph = target;
          this.snapTarget = null;
        } else {
          this.morph = next;
        }
        this.onChange?.(this.morph);
        this.layout();
      }

      this.drawPreview(sharedPhase(now));
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }
}

function drawAnchorGlyph(
  canvas: HTMLCanvasElement,
  glyph: GlyphFn,
  proximity: number,
  cellSize: number,
): void {
  const dpr = window.devicePixelRatio || 1;
  const px = cellSize * dpr;
  if (canvas.width !== px) {
    canvas.width = px;
    canvas.height = px;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cellSize, cellSize);

  ctx.save();
  // Stroke colour and glow swell with proximity. At p = 0 the glyph is the
  // dim "unselected" tone from the selector; at p = 1 it matches the ice-
  // blue selected look (shadow + filled stroke).
  const baseGray = "rgba(124, 135, 148, 0.85)";
  const iceR = 125,
    iceG = 211,
    iceB = 252;
  const strokeR = Math.round(124 + (iceR - 124) * proximity);
  const strokeG = Math.round(135 + (iceG - 135) * proximity);
  const strokeB = Math.round(148 + (iceB - 148) * proximity);
  if (proximity > 0.001) {
    ctx.shadowColor = `rgba(56, 189, 248, ${0.4 + proximity * 0.5})`;
    ctx.shadowBlur = 2 + proximity * 10;
    ctx.strokeStyle = `rgb(${strokeR}, ${strokeG}, ${strokeB})`;
  } else {
    ctx.strokeStyle = baseGray;
  }
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  glyph(ctx, cellSize);
  ctx.restore();
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
